import { useState, useEffect, useCallback, useMemo } from 'react'
import { useParams, Link, useLocation, useSearchParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { localizeApiDetail } from '../../../i18n/apiError'
import { ArrowLeft, Building2, Phone, Mail, Globe, MapPin, Calendar, Clock, Users, AlertTriangle } from 'lucide-react'
import { leadsAPI, bilerSearchFromParams } from '../api'
import type { OrgDetail, FollowUpResponse, BilerSearchFilters } from '../api'
import { formatNorwegianDate } from '../../../utils/dateUtils'
import LeadsSection from './LeadsSection'
import FollowUpSection from './FollowUpSection'
import VehiclesSection from './VehiclesSection'
import OvrigeKjoretoySection from './OvrigeKjoretoySection'
import RegnskapTab from './RegnskapTab'
import HendelserTab from './HendelserTab'
import FinansieringTab from './FinansieringTab'
import UnderenheterSection from './UnderenheterSection'
import { useAuth } from '../../../contexts/AuthContext'
import { hasAnyRole, ROLE_OPPSLAG_BASIC } from '../../../utils/roles'
import { useBookmarks } from '../useBookmarks'
import LeadsTableExport from '../LeadsTableExport'
import type { ColumnDefinition } from '../../../utils/exportToCsv'

export default function BedriftDetail() {
  const { t } = useTranslation('leads')
  // Return to the exact leads view the user came from — tab, page and
  // filters all live in that URL. Falls back to a bare /leads for direct
  // links and refreshes, where there is no view to return to.
  const backTo = ((useLocation().state as { from?: string } | null)?.from) ?? '/leads'
  const { orgNr } = useParams<{ orgNr: string }>()
  const [org, setOrg] = useState<OrgDetail | null>(null)
  const [followUps, setFollowUps] = useState<FollowUpResponse | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const { isBookmarked, toggle: toggleBookmark } = useBookmarks()
  const [activeTab, setActiveTab] = useState<'oversikt' | 'aktiviteter'>('oversikt')

  // An arrival from the Biler tab carries the search in the URL (b* params —
  // modell repeats because the strings can contain commas). The vehicle list
  // sorts the matching cars first and highlights them (#625 AC5); the params
  // living in the URL means a refresh keeps both.
  const [searchParams] = useSearchParams()
  const bilerFilters = useMemo<BilerSearchFilters | null>(
    () => bilerSearchFromParams(searchParams),
    [searchParams],
  )

  // Second-section tabs inside Oversikt (#326) — replaces the stacked scroll.
  // An arrival from the Biler tab lands straight on Kjøretøy so the highlight
  // the user came for is visible without an extra click.
  const [sectionTab, setSectionTab] = useState<'detaljer' | 'leads' | 'kjoretoy' | 'kjoretoyhendelser' | 'underenheter' | 'finansiering'>(
    () => (bilerFilters ? 'kjoretoy' : 'detaljer'),
  )
  // Finansiering (#943) needs the Oppslag basic role on top of Leads
  const { user } = useAuth()
  const kanSeFinansiering = hasAnyRole(user, [ROLE_OPPSLAG_BASIC])
  // Third level inside Kjøretøy (#640). Personbil og varebil stays the
  // default, so a Biler arrival still lands on its highlighted cars.
  const [vehicleTab, setVehicleTab] = useState<'personbil' | 'ovrige'>('personbil')

  const loadFollowUps = useCallback(() => {
    if (!orgNr) return
    leadsAPI.getFollowUps(orgNr)
      .then(res => setFollowUps(res.data))
      .catch(() => {})
  }, [orgNr])

  useEffect(() => {
    if (!orgNr) return
    setLoading(true)
    setError(null)
    Promise.all([
      leadsAPI.getOrgDetail(orgNr),
      leadsAPI.getFollowUps(orgNr),
    ])
      .then(([orgRes, fuRes]) => {
        setOrg(orgRes.data)
        setFollowUps(fuRes.data)
      })
      .catch(err => {
        const msg = localizeApiDetail(err.response?.data?.detail) || err.message
        setError(msg)
      })
      .finally(() => setLoading(false))
  }, [orgNr])

  if (loading) {
    return (
      <div className="space-y-4">
        <div className="animate-pulse h-8 w-48 bg-gray-200 dark:bg-gray-700 rounded-sm" />
        <div className="animate-pulse h-64 bg-gray-100 dark:bg-dark-border rounded-sm" />
      </div>
    )
  }

  if (error || !org) {
    return (
      <div className="space-y-4">
        <Link to={backTo} className="inline-flex items-center gap-1 text-primary-600 dark:text-primary-400 hover:underline text-sm">
          <ArrowLeft className="w-4 h-4" /> {t('bedrift.back')}
        </Link>
        <div className="card p-6 text-red-600 dark:text-red-400">{error || t('bedrift.notFound')}</div>
      </div>
    )
  }

  // One underenhet is usually the business itself, so the tab needs 2+ (#1002)
  const harUnderenheter = org.underenhet_count >= 2

  const statusLabel: Record<string, string> = {
    ny: t('followUp.status.ny'),
    kontaktet: t('followUp.status.kontaktet'),
    tilbud_sendt: t('followUp.status.tilbudSendt'),
    ikke_aktuell: t('followUp.status.ikkeAktuell'),
    vunnet: t('followUp.status.vunnet'),
  }
  const aktiviteterColumns: ColumnDefinition[] = [
    { key: 'created_at', label: t('common.dato'), format: v => (v ? formatNorwegianDate(v.split('T')[0] || v) : '—') },
    { key: 'user_id', label: t('bedrift.bruker'), format: v => String(v ?? '—') },
    { key: 'status', label: t('bedrift.status'), format: v => (v ? statusLabel[v] ?? v : '—') },
    { key: 'comment', label: t('bedrift.kommentar'), format: v => v ?? '' },
  ]

  return (
    <div className="space-y-6">
      {/* Back link */}
      <Link to={backTo} className="inline-flex items-center gap-1 text-primary-600 dark:text-primary-400 hover:underline text-sm">
        <ArrowLeft className="w-4 h-4" /> {t('bedrift.back')}
      </Link>

      {/* Header */}
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
            {org.navn}
            <button onClick={() => toggleBookmark(orgNr!)} className="text-2xl ml-2 hover:scale-110 transition-transform align-middle" title={isBookmarked(orgNr!) ? t('bedrift.removeBookmark') : t('bookmark.add')}>
              {isBookmarked(orgNr!) ? '\u2B50' : '\u2606'}
            </button>
          </h1>
          <div className="flex items-center gap-3 mt-1 text-sm text-gray-500 dark:text-gray-400">
            <span className="font-mono">{org.organisasjonsnummer}</span>
            {org.organisasjonsform_beskrivelse && (
              <span className="px-2 py-0.5 rounded-sm bg-gray-100 dark:bg-gray-800 text-xs">{org.organisasjonsform_beskrivelse}</span>
            )}
          </div>
        </div>
        {/* Status badges */}
        <div className="flex gap-2">
          {(org.konkurs === 1) && (
            <span className="inline-flex items-center gap-1 px-2 py-1 rounded-sm text-xs font-medium bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-300">
              <AlertTriangle className="w-3 h-3" /> {t('bedrift.konkurs')}
            </span>
          )}
          {(org.under_avvikling === 1) && (
            <span className="inline-flex items-center gap-1 px-2 py-1 rounded-sm text-xs font-medium bg-orange-100 dark:bg-orange-900/30 text-orange-800 dark:text-orange-300">
              <AlertTriangle className="w-3 h-3" /> {t('bedrift.underAvvikling')}
            </span>
          )}
        </div>
      </div>

      {/* KPI strip */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <div className="card p-4">
          <div className="text-xs text-gray-500 dark:text-gray-400">{t('bedrift.kpi.leadScore')}</div>
          <div className="text-2xl font-bold text-primary-600 dark:text-primary-400">{org.total_score ?? '\u2014'}</div>
        </div>
        <div className="card p-4">
          <div className="text-xs text-gray-500 dark:text-gray-400">{t('bedrift.kpi.aktiveLeads')}</div>
          <div className="text-2xl font-bold">{org.eligible_car_count ?? '\u2014'}</div>
        </div>
        <div className="card p-4">
          <div className="text-xs text-gray-500 dark:text-gray-400">{t('bedrift.kpi.hottesteKategori')}</div>
          <div className="text-sm font-bold mt-1">
            {org.hottest_category ? (
              <span className="inline-flex items-center px-2 py-0.5 rounded-sm text-xs bg-amber-100 dark:bg-amber-900/30 text-amber-800 dark:text-amber-300">{org.hottest_category}</span>
            ) : '\u2014'}
          </div>
        </div>
        <div className="card p-4">
          <div className="text-xs text-gray-500 dark:text-gray-400">{t('bedrift.kpi.oppfolgingsstatus')}</div>
          <div className="text-sm font-bold mt-1">
            {followUps?.summary ? (
              <StatusBadge status={followUps.summary.status} />
            ) : (
              <span className="text-gray-400">{t('bedrift.kpi.ikkeFulgtOpp')}</span>
            )}
          </div>
        </div>
      </div>

      {/* Top-level tab bar: Oversikt | Aktiviteter (#389 — Regnskap lives as a
          section inside Oversikt). Aktiviteter is the USER's activity on this
          bedrift; the org's vehicle events live under Oversikt > Kjøretøyhendelser. */}
      <div className="border-b border-gray-200 dark:border-gray-700">
        <nav className="flex gap-4">
          {([['oversikt', t('bedrift.tabs.oversikt')], ['aktiviteter', t('bedrift.tabs.aktiviteter')]] as const).map(([key, label]) => (
            <button
              key={key}
              onClick={() => setActiveTab(key)}
              className={`pb-2 text-sm font-medium border-b-2 transition-colors ${
                activeTab === key
                  ? 'border-primary-600 text-primary-600 dark:border-primary-400 dark:text-primary-400'
                  : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
              }`}
            >
              {label}
            </button>
          ))}
        </nav>
      </div>

      {/* Tab content */}
      {activeTab === 'oversikt' ? (
        <>
          {/* Second-section tabs — same underline UI as the top menu (#331),
              sitting directly under it */}
          <div className="border-b border-gray-200 dark:border-gray-700 -mt-2">
            <nav className="flex gap-4">
              {([
                ['detaljer', t('bedrift.tabs.detaljer')],
                ['leads', t('common.leads')],
                ['kjoretoy', t('bedrift.tabs.kjoretoy')],
                ['kjoretoyhendelser', t('bedrift.tabs.kjoretoyhendelser')],
                ...(harUnderenheter ? [['underenheter', t('bedrift.tabs.underenheter')]] as const : []),
                ...(kanSeFinansiering ? [['finansiering', t('bedrift.tabs.finansiering')]] as const : []),
              ] as const).map(([key, label]) => (
                <button
                  key={key}
                  onClick={() => setSectionTab(key)}
                  className={`pb-2 text-sm font-medium border-b-2 transition-colors ${
                    sectionTab === key
                      ? 'border-primary-600 text-primary-600 dark:border-primary-400 dark:text-primary-400'
                      : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
                  }`}
                >
                  {label}
                </button>
              ))}
            </nav>
          </div>

          {sectionTab === 'detaljer' && (
          <>
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Company info - 2 cols */}
            <div className="lg:col-span-2 card p-6">
              <h2 className="text-lg font-bold text-gray-900 dark:text-white mb-4">
                <Building2 className="w-5 h-5 inline mr-2" />{t('bedrift.info.title')}
              </h2>
              <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-3 text-sm">
                {org.naeringskode1_kode && (
                  <InfoRow label={t('common.nace')} value={`${org.naeringskode1_kode} \u2014 ${org.naeringskode1_beskrivelse || ''}`} />
                )}
                {org.antall_ansatte != null && (
                  <InfoRow label={t('bedrift.info.ansatte')} value={org.antall_ansatte.toLocaleString('nb-NO')} icon={<Users className="w-4 h-4" />} />
                )}
                {org.forretningsadresse_adresse && (
                  <InfoRow label={t('bedrift.info.adresse')} value={`${org.forretningsadresse_adresse}, ${org.forretningsadresse_postnummer} ${org.forretningsadresse_poststed}`} icon={<MapPin className="w-4 h-4" />} />
                )}
                {org.forretningsadresse_kommune && (
                  <InfoRow label={t('common.kommune')} value={org.forretningsadresse_kommune} />
                )}
                {org.telefon && (
                  <InfoRow label={t('bedrift.info.telefon')} value={org.telefon} icon={<Phone className="w-4 h-4" />} />
                )}
                {org.mobil && (
                  <InfoRow label={t('bedrift.info.mobil')} value={org.mobil} icon={<Phone className="w-4 h-4" />} />
                )}
                {org.epostadresse && (
                  <InfoRow label={t('bedrift.info.epost')} value={org.epostadresse} icon={<Mail className="w-4 h-4" />} />
                )}
                {org.hjemmeside && (
                  <InfoRow label={t('bedrift.info.hjemmeside')} value={org.hjemmeside} icon={<Globe className="w-4 h-4" />} link />
                )}
                {org.stiftelsesdato && (
                  <InfoRow label={t('bedrift.info.stiftet')} value={formatNorwegianDate(org.stiftelsesdato)} icon={<Calendar className="w-4 h-4" />} />
                )}
                {org.registreringsdato_enhetsregisteret && (
                  <InfoRow label={t('bedrift.info.registrert')} value={formatNorwegianDate(org.registreringsdato_enhetsregisteret)} icon={<Calendar className="w-4 h-4" />} />
                )}
              </dl>
            </div>

            {/* Follow-up form - 1 col; the history lives under Aktiviteter */}
            <div className="lg:col-span-1">
              <FollowUpSection orgNr={orgNr!} onFollowUpCreated={loadFollowUps} />
            </div>
          </div>

          {/* Regnskap as a section below the company info (#389) */}
          <div>
            <h2 className="text-lg font-bold text-gray-900 dark:text-white mb-4">{t('bedrift.regnskap')}</h2>
            <RegnskapTab orgNr={orgNr!} orgNavn={org.navn} />
          </div>
          </>
          )}

          {sectionTab === 'leads' && <LeadsSection orgNr={orgNr!} orgNavn={org.navn} />}

          {sectionTab === 'kjoretoy' && (
            <>
              <nav className="flex gap-2 -mt-2" data-testid="vehicle-subtabs">
                {([
                  ['personbil', t('bedrift.vehicleTabs.personbilVarebil')],
                  ['ovrige', t('bedrift.vehicleTabs.ovrige')],
                ] as const).map(([key, label]) => (
                  <button
                    key={key}
                    onClick={() => setVehicleTab(key)}
                    aria-pressed={vehicleTab === key}
                    className={`px-3 py-1 rounded-full text-sm font-medium transition-colors ${
                      vehicleTab === key
                        ? 'bg-primary-100 text-primary-700 dark:bg-primary-900/40 dark:text-primary-300'
                        : 'text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800'
                    }`}
                  >
                    {label}
                  </button>
                ))}
              </nav>
              {vehicleTab === 'personbil'
                ? <VehiclesSection orgNr={orgNr!} orgNavn={org.navn} bilerSearch={bilerFilters} />
                : <OvrigeKjoretoySection orgNr={orgNr!} orgNavn={org.navn} />}
            </>
          )}

          {sectionTab === 'kjoretoyhendelser' && <HendelserTab orgNr={orgNr!} orgNavn={org.navn} />}

          {sectionTab === 'underenheter' && harUnderenheter && <UnderenheterSection orgNr={orgNr!} orgNavn={org.navn} />}

          {sectionTab === 'finansiering' && kanSeFinansiering && <FinansieringTab orgNr={orgNr!} />}
        </>
      ) : (
        /* Aktiviteter: what the USER has done on this bedrift — the
           oppfølging log running down the page, with the form to add
           more on the right (#389) */
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 items-start">
          <div className="lg:col-span-2 card p-6">
            <h2 className="text-lg font-bold text-gray-900 dark:text-white mb-4">
              <Clock className="w-5 h-5 inline mr-2" />{t('bedrift.aktiviteter')}
              {followUps && followUps.history.length > 0 && (
                <span className="text-sm font-normal text-gray-500 ml-2">({followUps.history.length})</span>
              )}
            </h2>
            {followUps && followUps.history.length > 0 ? (
              <>
              <LeadsTableExport
                rows={followUps.history}
                columns={aktiviteterColumns}
                title={t('tableExport.orgTitle', { table: t('bedrift.aktiviteter'), navn: org.navn ?? orgNr, orgNr })}
                testId="bedrift-aktiviteter-export"
              />
              <div className="overflow-x-auto">
                <table className="data-table data-table-compact w-full">
                  <thead>
                    <tr>
                      <th className="text-left whitespace-nowrap">{t('common.dato')}</th>
                      <th className="text-left whitespace-nowrap">{t('bedrift.bruker')}</th>
                      <th className="text-left whitespace-nowrap">{t('bedrift.status')}</th>
                      <th className="text-left">{t('bedrift.kommentar')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {followUps.history.map(entry => (
                      <tr key={entry.id}>
                        <td className="whitespace-nowrap">
                          {entry.created_at ? formatNorwegianDate(entry.created_at.split('T')[0] || entry.created_at) : '—'}
                        </td>
                        <td className="whitespace-nowrap">{entry.user_id}</td>
                        <td className="whitespace-nowrap">{entry.status ? <StatusBadge status={entry.status} /> : '—'}</td>
                        <td>{entry.comment}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              </>
            ) : (
              <p className="text-sm text-gray-500 dark:text-gray-400">{t('bedrift.noActivities')}</p>
            )}
          </div>

          <div className="lg:col-span-1">
            <FollowUpSection orgNr={orgNr!} onFollowUpCreated={loadFollowUps} />
          </div>
        </div>
      )}
    </div>
  )
}


export function StatusBadge({ status }: { status: string }) {
  const { t } = useTranslation('leads')
  const colors: Record<string, string> = {
    ny: 'bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-300',
    kontaktet: 'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-800 dark:text-yellow-300',
    tilbud_sendt: 'bg-purple-100 dark:bg-purple-900/30 text-purple-800 dark:text-purple-300',
    ikke_aktuell: 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300',
    vunnet: 'bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300',
  }
  const labels: Record<string, string> = {
    ny: t('followUp.status.ny'),
    kontaktet: t('followUp.status.kontaktet'),
    tilbud_sendt: t('followUp.status.tilbudSendt'),
    ikke_aktuell: t('followUp.status.ikkeAktuell'),
    vunnet: t('followUp.status.vunnet'),
  }
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded-sm text-xs font-medium ${colors[status] || colors.ny}`}>
      {labels[status] || status}
    </span>
  )
}


function InfoRow({ label, value, icon, link }: { label: string; value: string; icon?: React.ReactNode; link?: boolean }) {
  return (
    <div>
      <dt className="text-gray-500 dark:text-gray-400 text-xs">{label}</dt>
      <dd className="flex items-center gap-1 mt-0.5">
        {icon && <span className="text-gray-400">{icon}</span>}
        {link ? (
          <a href={value.startsWith('http') ? value : `https://${value}`} target="_blank" rel="noopener noreferrer"
             className="text-primary-600 dark:text-primary-400 hover:underline">{value}</a>
        ) : (
          <span>{value}</span>
        )}
      </dd>
    </div>
  )
}
